import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { BRAND_ASSETS } from '../../core/constants/brand-assets';

type TwoFactorMethod = '2fa-sms' | '2fa-totp';

interface Enable2FAResponse {
  success: boolean;
  message: string;
  method: TwoFactorMethod;
  qrCode?: string; // QR code data URL for TOTP
  backupCodes: string[];
}

@Component({
  selector: 'app-setup-2fa',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './setup-2fa.component.html',
  styleUrl: './setup-2fa.component.scss'
})
export class Setup2faComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  setupForm: FormGroup;
  loading = false;
  errorMessage = '';
  step: 'method-selection' | 'totp-setup' | 'backup-codes' = 'method-selection';
  selectedMethod: TwoFactorMethod = '2fa-totp';
  qrCodeDataUrl = '';
  backupCodes: string[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient
  ) {
    this.setupForm = this.fb.group({
      method: ['2fa-totp', Validators.required],
      phoneNumber: [''], // Only required for SMS
      verificationCode: [''] // For verifying TOTP setup
    });
  }

  ngOnInit(): void {
    // Listen to method changes to update phone number requirement
    this.setupForm.get('method')?.valueChanges.subscribe((method: TwoFactorMethod) => {
      this.selectedMethod = method;
      if (method === '2fa-sms') {
        this.setupForm.get('phoneNumber')?.setValidators([Validators.required, Validators.pattern(/^\+?[1-9]\d{1,14}$/)]);
      } else {
        this.setupForm.get('phoneNumber')?.clearValidators();
      }
      this.setupForm.get('phoneNumber')?.updateValueAndValidity();
    });
  }

  async onMethodSubmit(): Promise<void> {
    if (this.selectedMethod === '2fa-sms' && this.setupForm.get('phoneNumber')?.invalid) {
      this.setupForm.get('phoneNumber')?.markAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const payload: any = {
        method: this.selectedMethod
      };

      if (this.selectedMethod === '2fa-sms') {
        payload.phoneNumber = this.setupForm.get('phoneNumber')?.value;
      }

      const response = await firstValueFrom(
        this.http.post<Enable2FAResponse>(`${environment.apiUrl}/auth/2fa/enable`, payload)
      );

      if (response.success) {
        this.backupCodes = response.backupCodes;

        if (this.selectedMethod === '2fa-totp' && response.qrCode) {
          this.qrCodeDataUrl = response.qrCode;
          this.step = 'totp-setup';
          this.setupForm.get('verificationCode')?.setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(6)]);
          this.setupForm.get('verificationCode')?.updateValueAndValidity();
        } else {
          // SMS method, go directly to backup codes
          this.step = 'backup-codes';
        }
      }
    } catch (error: any) {
      console.error('Enable 2FA error:', error);
      this.errorMessage = error.error?.error || 'Failed to enable 2FA. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  async onVerifyTOTP(): Promise<void> {
    if (this.setupForm.get('verificationCode')?.invalid) {
      this.setupForm.get('verificationCode')?.markAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      // Verify the TOTP code to ensure setup is correct
      const code = this.setupForm.get('verificationCode')?.value;

      // We'll use the verify endpoint to test if the code works
      // In a real implementation, you might have a separate "verify setup" endpoint
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; message: string }>(
          `${environment.apiUrl}/auth/2fa/verify`,
          {
            email: '', // Email will come from the auth token
            code: code.trim()
          }
        )
      );

      if (response.success) {
        // TOTP verified successfully, show backup codes
        this.step = 'backup-codes';
      } else {
        this.errorMessage = 'Invalid verification code. Please try again.';
      }
    } catch (error: any) {
      console.error('Verify TOTP error:', error);
      this.errorMessage = 'Invalid verification code. Please make sure your authenticator app is synchronized.';
    } finally {
      this.loading = false;
    }
  }

  downloadBackupCodes(): void {
    const codesText = this.backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'adn-backup-codes.txt';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  copyBackupCodes(): void {
    const codesText = this.backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
  }

  finishSetup(): void {
    // Navigate to settings or dashboard
    this.router.navigate(['/settings']);
  }

  cancel(): void {
    // Navigate back to settings
    this.router.navigate(['/settings']);
  }
}
